/*
Industries hub (/industries). A short, warm index of the industry landing pages.
Operator-facing voice — see the marketing-voice notes.
*/
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Card, Container, Grid, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import useSeo from "utils/useSeo";
import { pageDescription } from "utils/routeMeta";
import { INDUSTRIES } from "./industriesData.js";
import RouteSheetCanvas from "./RouteSheetCanvas.jsx";

const Eyebrow = ({ children, center }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, justifyContent: center ? "center" : "flex-start" }}>
    <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: "primary.main" }} />
    <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "primary.main" }}>{children}</Typography>
  </Box>
);

export default function IndustriesHub() {
  useSeo({
    title: "Industries",
    description: pageDescription('/industries'),
    canonicalPath: "/industries",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box sx={{ backgroundColor: "#ffffff" }}>
      <Box sx={{ background: "linear-gradient(180deg, rgba(230, 252, 251, 0.45) 0%, #FFFFFF 100%)", pt: { xs: 5, md: 8 }, pb: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720 }}>
            <Eyebrow>Industries</Eyebrow>
            <Typography variant="h1" sx={{ fontSize: { xs: "2.1rem", md: "3rem" }, fontWeight: 800, color: "dark.main", lineHeight: 1.1, mt: 2, mb: 2.5 }}>
              Built for the way your day actually runs.
            </Typography>
            <Typography sx={{ fontSize: "1.15rem", color: "text.secondary", lineHeight: 1.55, maxWidth: 560 }}>
              The rules are different in every line of work — appointment windows, viability deadlines, a two-hour promise. Pick the one that sounds like your day and see the tool handle it, on a real map.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 9 } }}>
        <Grid container spacing={3} sx={{ mt: { xs: 0, md: 1 } }}>
          {INDUSTRIES.map((ind) => (
            <Grid item xs={12} md={4} key={ind.slug}>
              <Card
                variant="outlined"
                component={Link}
                to={`/industries/${ind.slug}`}
                sx={{ height: "100%", borderRadius: 4, overflow: "hidden", display: "flex", flexDirection: "column", textDecoration: "none", transition: "all .18s", "&:hover": { borderColor: "primary.main", transform: "translateY(-3px)", boxShadow: "0 18px 40px -28px rgba(43,55,66,.35)" } }}
              >
                <Box sx={{ p: 2.5, backgroundColor: "#f6f4ef", borderBottom: "1px solid", borderColor: "divider" }}>
                  <RouteSheetCanvas {...ind.heroCanvas} aspect="16 / 9" label={`${ind.navLabel} route`} />
                </Box>
                <Box sx={{ p: 3, display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "primary.main", mb: 0.75 }}>{ind.eyebrow}</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: "dark.main", lineHeight: 1.2, mb: 1.25 }}>{ind.navLabel}</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.95rem", lineHeight: 1.55 }}>{ind.cardBlurb}</Typography>
                  <Typography sx={{ mt: "auto", pt: 2.5, fontWeight: 700, fontSize: "0.92rem", color: "primary.main", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    Explore <ArrowForwardIcon sx={{ fontSize: 17 }} />
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: "center", mt: { xs: 6, md: 9 }, pt: { xs: 4, md: 6 }, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", md: "2rem" }, color: "dark.main", maxWidth: 520, mx: "auto" }}>
            Don't see your line of work?
          </Typography>
          <Typography sx={{ mt: 2, color: "text.secondary", fontSize: "1.05rem", lineHeight: 1.55, maxWidth: 500, mx: "auto" }}>
            The same engine handles snow plows, ski shuttles, ATM runs, and dozens more. Browse the full set of examples, or just try it with your own stops.
          </Typography>
          <Box sx={{ mt: 3.5, display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Button component={Link} to="/use-cases" variant="contained" endIcon={<ArrowForwardIcon />}
              sx={{ backgroundColor: "primary.main", px: 3.5, py: 1.35, fontSize: "1rem", "&:hover": { backgroundColor: "primary.extraDark" } }}>
              Browse all examples
            </Button>
            <Button component={Link} to="/model-builder" variant="outlined"
              sx={{ px: 3.5, py: 1.35, fontSize: "1rem", borderColor: "divider", color: "dark.main", "&:hover": { borderColor: "primary.main", color: "primary.main" } }}>
              Try it with your jobs
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
